// frontend/src/services/notificationService.js
import apiService from './api';

class NotificationService {
  async getNotifications(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiService.get(`/notifications${queryString ? `?${queryString}` : ''}`);
      return {
        success: true,
        data: response.data || response,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch notifications',
        data: { notifications: [], unreadCount: 0 }
      };
    }
  }

  async getUnreadCount() {
    try {
      const response = await apiService.get('/notifications/unread-count');
      return {
        success: true,
        data: response.data || response,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch unread count',
        data: { count: 0 }
      };
    }
  }

  async markAsRead(notificationId) {
    try {
      const response = await apiService.patch(`/notifications/${notificationId}/read`);
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Notification marked as read'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to mark notification as read'
      };
    }
  }

  async markAllAsRead() {
    try {
      const response = await apiService.patch('/notifications/read-all');
      return {
        success: true,
        message: response.message || 'All notifications marked as read'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to mark all as read'
      };
    }
  }

  async deleteNotification(notificationId) {
    try {
      const response = await apiService.delete(`/notifications/${notificationId}`);
      return {
        success: true,
        message: response.message || 'Notification deleted'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to delete notification'
      };
    }
  }

  getNotificationIcon(type) {
    const icons = {
      appointment_reminder: '📅',
      appointment_confirmed: '✅',
      appointment_cancelled: '❌',
      consultation_request: '💬',
      consultation_accepted: '✅',
      consultation_rejected: '❌',
      consultation_completed: '✔️',
      prescription_ready: '💊',
      order_placed: '🛒',
      order_confirmed: '✅',
      order_preparing: '👨‍🍳',
      order_dispatched: '🚚',
      order_delivered: '📦',
      order_cancelled: '❌',
      new_order: '🔔',
      payment_received: '💰',
      payment_failed: '❌',
      ambulance_dispatched: '🚑',
      system_update: '🔔',
      account_verified: '✅',
      subscription_expiring: '⚠️',
      default: '📬'
    };
    return icons[type] || icons.default;
  }

  getNotificationColor(priority) {
    const colors = {
      low: 'gray',
      normal: 'blue',
      high: 'orange',
      urgent: 'red'
    };
    return colors[priority] || colors.normal;
  }

  getPriorityBadgeClass(priority) {
    const classes = {
      low: 'bg-gray-100 text-gray-700',
      normal: 'bg-blue-100 text-blue-700',
      high: 'bg-orange-100 text-orange-700',
      urgent: 'bg-red-100 text-red-700'
    };
    return classes[priority] || classes.normal;
  }

  getNotificationTypeLabel(type) {
    const labels = {
      appointment_reminder: 'Appointment Reminder',
      appointment_confirmed: 'Appointment Confirmed',
      appointment_cancelled: 'Appointment Cancelled',
      consultation_request: 'Consultation Request',
      consultation_accepted: 'Consultation Accepted',
      consultation_rejected: 'Consultation Rejected',
      consultation_completed: 'Consultation Completed',
      prescription_ready: 'Prescription Ready',
      order_placed: 'Order Placed',
      order_confirmed: 'Order Confirmed',
      order_preparing: 'Order Being Prepared',
      order_dispatched: 'Order Dispatched',
      order_delivered: 'Order Delivered',
      order_cancelled: 'Order Cancelled',
      new_order: 'New Order',
      payment_received: 'Payment Received',
      payment_failed: 'Payment Failed',
      ambulance_dispatched: 'Ambulance Dispatched',
      system_update: 'System Update',
      account_verified: 'Account Verified',
      subscription_expiring: 'Subscription Expiring',
      default: 'Notification'
    };
    return labels[type] || labels.default;
  }
}

const notificationService = new NotificationService();
export { notificationService };
export default notificationService;